import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ensureTranslations,
  readCachedTranslation,
} from '../../../firebase/translations';
import { silentCatch } from '../../../utils/errorHandler';

/**
 * Resolves the viewer-facing name + description for a product on the detail page.
 *
 * - When the viewer's language matches the product's original language, the
 *   original text is shown (mode 'original', toggle hidden).
 * - Otherwise the cached translation is used if present; on a miss the
 *   `translateAds` callable is invoked and, while pending, the original text
 *   is shown (ready:false). The viewer can flip between original and
 *   translation via `showOriginal`.
 *
 * Returns { name, description, mode, showOriginal, setShowOriginal, ready,
 *           isTranslatable }.
 */
export function useProductTranslation(product) {
  const { i18n } = useTranslation();
  const lang = i18n?.language || 'en';
  const originalLanguage = product?.originalLanguage || 'bg';
  const isTranslatable = Boolean(
    product?.id && lang && lang !== originalLanguage
  );

  const cached = useMemo(
    () => (isTranslatable ? readCachedTranslation(product, lang) : null),
    [product, lang, isTranslatable]
  );

  const [translated, setTranslated] = useState(cached);
  const [ready, setReady] = useState(Boolean(cached));
  const [showOriginal, setShowOriginal] = useState(false);

  useEffect(() => {
    if (!isTranslatable) {
      setTranslated(null);
      setReady(true);
      return;
    }
    if (cached) {
      setTranslated(cached);
      setReady(true);
      return;
    }
    setTranslated(null);
    setReady(false);
    let cancelled = false;
    ensureTranslations([product.id], lang)
      .then((res) => {
        if (cancelled) return;
        const entry = res && res[product.id];
        if (entry) setTranslated(entry);
        setReady(true);
      })
      .catch(silentCatch('useProductTranslation:ensureTranslations'));
    return () => {
      cancelled = true;
    };
  }, [product, lang, cached, isTranslatable]);

  const usingTranslation = isTranslatable && translated && !showOriginal;
  const name = usingTranslation
    ? translated.name != null ? translated.name : product.name
    : product.name;
  const description = usingTranslation
    ? translated.description != null ? translated.description : product.description
    : product.description;
  const mode = usingTranslation ? 'translation' : 'original';

  return {
    name: name || '',
    description: description || '',
    mode,
    showOriginal,
    setShowOriginal,
    ready,
    isTranslatable,
  };
}
